import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { CheckCircle, Close } from '@mui/icons-material'
import { Chip, IconButton, Stack, Typography } from '@mui/material'

import { ScreenshotConcept, ScreenshotFeature } from '../../types/AnalysisScreenshots'

export type FeatureRow = { feature: ScreenshotFeature; count?: number; marked?: boolean; pending?: boolean }
export type ConceptRow = { concept: ScreenshotConcept; count?: number; marked?: boolean; pending?: boolean }

type ScreenshotFeatureListProps = {
  features: FeatureRow[]
  concepts: ConceptRow[]
  unknownConceptIds?: string[]
  // With several screenshots, each row tells on how many of them it is.
  total?: number
  disabled?: boolean
  onRemoveFeature?: (row: FeatureRow) => void
  onRemoveConcept?: (row: ConceptRow) => void
}

type RowProps = {
  label: string
  detail?: string
  count?: number
  total?: number
  marked?: boolean
  pending?: boolean
  removeLabel?: string
  disabled?: boolean
  onRemove?: () => void
}

const Row: FC<RowProps> = ({ label, detail, count, total, marked, pending, removeLabel, disabled, onRemove }) => {
  const { t } = useTranslation()
  return (
    <Stack direction="row" alignItems="center" spacing={1} minHeight={32}>
      {marked !== undefined && <CheckCircle fontSize="small" color={marked ? 'success' : 'disabled'} aria-hidden />}
      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
        {label}
        {detail && (
          <Typography component="span" variant="body2" fontWeight="bold">
            {`: ${detail}`}
          </Typography>
        )}
      </Typography>
      {total !== undefined && total > 1 && count !== undefined && <Chip size="small" variant="outlined" label={`${count}/${total}`} />}
      {pending && <Chip size="small" color="warning" label={t('internal-analysts:analysis_screenshots_not_saved')} />}
      {onRemove && (
        <IconButton size="small" aria-label={removeLabel} disabled={disabled} onClick={onRemove}>
          <Close fontSize="small" />
        </IconButton>
      )}
    </Stack>
  )
}

export const ScreenshotFeatureList: FC<ScreenshotFeatureListProps> = ({
  features,
  concepts,
  unknownConceptIds = [],
  total,
  disabled,
  onRemoveFeature,
  onRemoveConcept,
}) => {
  const { t } = useTranslation()

  return (
    <Stack spacing={1.5}>
      <Stack>
        <Typography variant="subtitle2" fontWeight="bold">
          {t('internal-analysts:analysis_screenshots_features')}
        </Typography>
        {features.length ? (
          features.map((row) => (
            <Row
              key={`${row.feature.legacyId}:${row.feature.weightId}:${!!row.pending}`}
              label={row.feature.name}
              detail={row.feature.choice}
              count={row.count}
              total={total}
              marked={row.marked}
              pending={row.pending}
              removeLabel={t('internal-analysts:analysis_screenshots_remove_feature_label', { name: row.feature.name })}
              disabled={disabled}
              onRemove={onRemoveFeature && (() => onRemoveFeature(row))}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:analysis_screenshots_no_features')}
          </Typography>
        )}
      </Stack>
      <Stack>
        <Typography variant="subtitle2" fontWeight="bold">
          {t('internal-analysts:analysis_screenshots_concepts')}
        </Typography>
        {concepts.length || unknownConceptIds.length ? (
          <>
            {concepts.map((row) => (
              <Row
                key={`${row.concept.id}:${!!row.pending}`}
                label={row.concept.name}
                count={row.count}
                total={total}
                marked={row.marked}
                pending={row.pending}
                removeLabel={t('internal-analysts:analysis_screenshots_remove_concept_label', { name: row.concept.name })}
                disabled={disabled}
                onRemove={onRemoveConcept && (() => onRemoveConcept(row))}
              />
            ))}
            {unknownConceptIds.map((id) => (
              <Typography key={id} variant="body2" color="text.secondary">
                {t('internal-analysts:analysis_screenshots_unknown_concept', { id })}
              </Typography>
            ))}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('internal-analysts:analysis_screenshots_no_concepts')}
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}
